import { useQuery } from '@tanstack/react-query';
import { BASE } from './api';

export type ServiceState = 'operational' | 'degraded' | 'unreachable';

export interface ServiceStatus {
  state: ServiceState;
  checkedAt: Date;
}

/** Reads the public health endpoint. A 503 still carries a JSON body, so degraded and unreachable are told apart. */
async function fetchServiceStatus(): Promise<ServiceStatus> {
  try {
    const res = await fetch(`${BASE}/api/v1/health`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    const body = (await res.json().catch(() => null)) as { status?: string } | null;
    const state: ServiceState = res.ok && body?.status === 'ok' ? 'operational' : body ? 'degraded' : 'unreachable';
    return { state, checkedAt: new Date() };
  } catch {
    return { state: 'unreachable', checkedAt: new Date() };
  }
}

/** Service status for the sign-in footer; polls once a minute while the page is open. */
export function useServiceStatus() {
  return useQuery({
    queryKey: ['service-status'],
    queryFn: fetchServiceStatus,
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
}

/** Public status page, served by this app at /status (the operations console has the same page). */
export const STATUS_PAGE_URL = '/status';

/**
 * Public status feed (no sign-in): the admin API's GET /admin/v1/status. Locally the admin API listens on 4100;
 * in Azure set VITE_STATUS_API_URL to the admin Functions host + /admin/v1/status.
 */
/*
 * The localhost fallback is for `vite dev` only: a production build without VITE_STATUS_API_URL gets an empty
 * URL and the page says the feed is not configured, instead of quietly calling localhost from every visitor's browser.
 */
export const STATUS_FEED_URL = ((import.meta.env['VITE_STATUS_API_URL'] as string | undefined) ?? (import.meta.env.DEV ? 'http://localhost:4100/admin/v1/status' : '')).replace(/\/$/, '');
